import type { QueueState, QueueVideo } from './model.ts';

/**
 * What one browser tab hands another.
 *
 * A queue lives in one tab and dies with it (`queue/model.ts`), which is the whole point of it and
 * also its one cost: a reader who has three Visious tabs open has three lists and can watch one of
 * them. The import is the answer — one tab asks, the others hand over what they were playing, and
 * the tabs that gave something up close.
 *
 * Everything here is a decision about two lists of videos, so it is core's and it is tested rather
 * than driven in a browser. What the platform owns is the part that is not a decision: finding the
 * other tabs, carrying the videos between them, and closing a window
 * (`apps/web/src/tabs/channel.ts`).
 *
 * **A linked queue is left out of this entirely.** A window on a playlist is that playlist's rows,
 * re-projected from the run in hand every time a page lands (`queueFromPlaylist`), so a row of the
 * tab's own added to one is a row the next page deletes. Which tabs are asked at all is the
 * platform's to enforce; what this file guarantees is that nothing it returns depends on a link.
 */

/**
 * A row with nothing behind it — the blanked playlist entry `queueVideosToSave` leaves out for the
 * same reason. Neither an id nor a title is something the receiving tab can draw a row from.
 */
function isBlank(video: QueueVideo): boolean {
  return video.videoId === '' || video.title.trim() === '';
}

/** The video half alone, since an entry carries a row key and two linked fields that do not travel. */
function toVideo(video: QueueVideo): QueueVideo {
  return {
    videoId: video.videoId,
    title: video.title,
    author: video.author,
    authorHref: video.authorHref,
    thumbnailUrl: video.thumbnailUrl,
    durationSec: video.durationSec,
  };
}

/**
 * What this tab has to give: its queue, plus the video it is playing when that video is not in the
 * queue.
 *
 * **The queue's order is kept**, because it is the order the reader put the rows in, and the playing
 * video leads only when the list does not already place it. A tab playing a video with no queue at
 * all therefore hands over exactly that one video, which is the common case: most tabs are a watch
 * page somebody left open.
 *
 * **Each video once.** A queue may hold the same video twice — one of the two things this list can
 * do that a playlist here cannot — but the import is a reader collecting what they had open, and two
 * identical rows arriving out of it is noise rather than a list they built.
 *
 * An empty answer is how a tab says it has nothing to give, and the platform reads it as a tab that
 * must **not** be closed: a tab parked on a feed or on the settings page is not one of the tabs the
 * videos came from.
 */
export function queueVideosToHandOver(
  state: QueueState,
  playing: QueueVideo | null,
): readonly QueueVideo[] {
  const send: QueueVideo[] = [];
  const seen = new Set<string>();
  const offer = (video: QueueVideo): void => {
    if (isBlank(video) || seen.has(video.videoId)) return;
    seen.add(video.videoId);
    send.push(toVideo(video));
  };
  if (playing !== null && !state.entries.some((entry) => entry.videoId === playing.videoId)) {
    offer(playing);
  }
  for (const entry of state.entries) offer(entry);
  return send;
}

/**
 * What of an offer is worth adding: the videos in the order they arrived, without the ones this tab
 * already has.
 *
 * `held` is whatever will still be in the list when the import lands — the rows that survive the act
 * the import is making, plus the video that is playing. The caller has to answer it rather than this
 * function reading a state, because two of the four acts below **replace** the list, and deduping
 * against rows that are about to go would drop videos for no reason.
 *
 * A video the reader has open twice is one video, which is why `held` grows as the list is walked.
 */
export function queueVideosToImport(
  offered: readonly QueueVideo[],
  held: ReadonlySet<string>,
): readonly QueueVideo[] {
  const take: QueueVideo[] = [];
  const seen = new Set(held);
  for (const video of offered) {
    if (isBlank(video) || seen.has(video.videoId)) continue;
    seen.add(video.videoId);
    take.push(toVideo(video));
  }
  return take;
}

/**
 * What the importing tab does with what it collected.
 *
 * - **`append`** — a list of this tab's own is already running, so the videos go on the end of it and
 *   nothing about the playback changes. A list that had played itself out moves on to the first of
 *   them ({@link queueAppendAndPlay}), since that is what "and start playing" means for a queue that
 *   had stopped.
 * - **`unlinkAndAppend`** — the reader chose to keep a playlist they were watching through. The rows
 *   in hand become a list of their own ({@link queueUnlink}) and the import goes on the end of those.
 *   The link has to go: a window on a playlist is re-projected from the playlist on the next page
 *   that lands, and the imported rows would be re-projected away with it.
 * - **`startWithCurrent`** — there is no list of this tab's own but something is playing, so the
 *   queue becomes that video followed by the import. The playing video leads so that nothing about
 *   the playback changes, which is `decideQueueAdd`'s rule for the same situation, and it is also
 *   what keeps a watch route's own video in the list it is now driving.
 * - **`start`** — nothing is playing and there is no list, so the import *is* the queue and it plays.
 *
 * `unlink` is the reader's answer to the one question the confirmation asks, and it is only ever
 * asked while this tab's queue is a window on a playlist. Left unanswered — the plain import — that
 * playlist is let go, which is what the other three arms all do to a link.
 */
export type QueueImportAction = 'append' | 'unlinkAndAppend' | 'startWithCurrent' | 'start';

export function decideQueueImport(input: {
  readonly queue: QueueState;
  /**
   * What the watch page or the corner player is on, and `null` when nothing is playing. The id
   * rather than a flag, for `decideQueueAdd`'s reason: the two starting arms are told apart by
   * whether there is a video to lead the list with.
   */
  readonly playingVideoId: string | null;
  /** The reader chose to keep the playlist this tab was watching through. */
  readonly unlink: boolean;
}): QueueImportAction {
  const { queue } = input;
  if (queue.link !== null && input.unlink) return 'unlinkAndAppend';
  if (queue.link === null && queue.entries.length > 0) return 'append';
  if (input.playingVideoId === null) return 'start';
  return 'startWithCurrent';
}
